import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { ApiError, api, can, type Issue, type Me, type TierPreview, type VendorRequestPolicy } from '../lib/api'
import { Disclosure, StickyFooter, Toggletip } from '../components/kit'
import { checkContractDates, todayIndia, warnContractEnded, warnContractStartFar, warnShortContract } from '../lib/dateRules'
import { DateWarnNote } from '../components/DateRuleNote'
import { registerAfterAdding, rememberAdded } from '../lib/justAdded'
import { usePeopleContacts } from '../lib/people'
import {
  advancedSummary,
  fieldLabel,
  approveOffered,
  checkAdd,
  checkApproveNow,
  createBody,
  DATA_KINDS,
  EMPTY_ADD,
  heldLine,
  INDIAN_MOBILE,
  ISSUE_FIELD,
  LEVELS,
  levelForErrors,
  mobileForApi,
  readLevel,
  registrationField,
  tierAnswered,
  tierInputs,
  toggleData,
  writeLevel,
  type AddForm,
  type Level,
} from '../lib/addVendor'
import '../styles/vendor-add.css'

// Add a vendor (release 3.3, the mentor's fourth point, task M6).
//
// A short form in rows, label beside field, in three levels of detail chosen
// at the top: Basic (six fields), Advanced (what decides the tier, and the
// contract dates) and Expert (the statutory numbers, address, bank and
// profile). The rules (what is required, which level a field is on, what is
// sent, what the tier is asked) are in lib/addVendor.ts, where the tests call
// them. Nothing statutory is ever required here: a number left blank becomes a
// gap on the vendor, to fill before it is approved.
//
// The tier is shown beside the form as the person answers, from the tenant's
// own rules on the server (POST /v1/vendors/tier-preview), so it is the tier
// the vendor gets.
//
// Where the person lands (release 3.3, the mentor's second point): "Add
// vendor" goes to the register with the new vendor pinned at the top and a
// confirmation naming it, replacing this form in the history so Back does not
// reopen it (rule B9). "Add another" keeps the form, emptied, for the next
// one. Neither opens the send screen: the questionnaire is a later step.

const VENDOR_TYPES: [string, string][] = [
  ['SERVICE_PROVIDER', 'Service provider'],
  ['PRODUCT_SUPPLIER', 'Product supplier'],
  ['SOFTWARE', 'Software'],
  ['CLOUD_SERVICE', 'Cloud service'],
  ['PROFESSIONAL_SERVICES', 'Professional services'],
  ['STAFFING', 'Staffing'],
  ['LOGISTICS', 'Logistics'],
  ['FACILITIES', 'Facilities'],
  ['FINANCIAL_SERVICES', 'Financial services'],
  ['GOVERNMENT_BODY', 'Government body'],
  ['OTHER', 'Other'],
]

const LEGAL_FORMS: [string, string][] = [
  ['PRIVATE_LIMITED', 'Private limited company'],
  ['PUBLIC_LIMITED', 'Public limited company'],
  ['LLP', 'Limited liability partnership'],
  ['PARTNERSHIP', 'Partnership firm'],
  ['PROPRIETORSHIP', 'Proprietorship'],
  ['TRUST', 'Trust'],
  ['SOCIETY', 'Society or association'],
  ['HUF', 'Hindu undivided family'],
  ['FOREIGN_ENTITY', 'Foreign entity'],
  ['GOVERNMENT_BODY', 'Government body'],
]

const FLAGS: [keyof AddForm, string][] = [
  ['crossBorder', 'Data moves outside India'],
  ['criticalBusinessService', 'Critical business service'],
  ['productionAccess', 'Access to production'],
  ['regulatedProcess', 'Regulated process'],
]

const HML: [string, string][] = [
  ['HIGH', 'High'],
  ['MEDIUM', 'Medium'],
  ['LOW', 'Low'],
]

// Mirrors the API's inference so the dropdown shows what the server will
// assume. The server decides; this only pre-fills.
export function guessLegalForm(name: string, country: string): string {
  const n = name.trim()
  if (!n) return ''
  const foreign = country !== '' && country !== 'IN'
  if (/\b(llp|limited liability partnership)\b/i.test(n)) return 'LLP'
  if (/\bhuf\b|hindu undivided family/i.test(n)) return 'HUF'
  if (/\b(government|govt\.?|ministry|department of|municipal|authority)\b/i.test(n)) return 'GOVERNMENT_BODY'
  if (/\btrust\b/i.test(n)) return 'TRUST'
  if (/\b(society|association|foundation)\b/i.test(n)) return 'SOCIETY'
  if (foreign) return 'FOREIGN_ENTITY'
  if (/\b(private|pvt\.?)\s*(limited|ltd\.?)\b/i.test(n)) return 'PRIVATE_LIMITED'
  if (/\b(inc\.?|corp\.?|gmbh|llc|plc|pte\.?|b\.?v\.?)\b/i.test(n)) return 'FOREIGN_ENTITY'
  if (/\b(limited|ltd\.?)\s*$/i.test(n)) return 'PUBLIC_LIMITED'
  return ''
}

export { mobileForApi }

const PREFIX = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0 9px',
  border: '1px solid var(--line)',
  borderRight: 0,
  borderRadius: 'var(--radius) 0 0 var(--radius)',
  background: 'var(--surface-2)',
  color: 'var(--ink-2)',
  fontVariantNumeric: 'tabular-nums',
} as const

const fid = (k: keyof AddForm) => 'va-' + k

// One row of the form: the label in the left column, the field or fields in
// the right, and under them the error or a short hint. A row with two fields
// names each field for a screen reader and the row as a group.
function Row({
  label,
  htmlFor,
  optional,
  hint,
  children,
  group,
}: {
  label: ReactNode
  htmlFor?: string
  optional?: boolean
  hint?: ReactNode
  children: ReactNode
  group?: string
}) {
  const lab = (
    <>
      {label}
      {optional && <span className="va-opt"> (optional)</span>}
    </>
  )
  return (
    <div className="va-row" role={group ? 'group' : undefined} aria-labelledby={group}>
      {htmlFor ? (
        <label className="va-label" htmlFor={htmlFor}>
          {lab}
        </label>
      ) : (
        <span className="va-label" id={group}>
          {lab}
        </span>
      )}
      <div className="va-cell">
        {children}
        {hint && <p className="va-hint">{hint}</p>}
      </div>
    </div>
  )
}

// One of two fields side by side in a row, with its own short label above it,
// so neither field leans on a placeholder or an option's words for its name.
function Half({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="va-half">
      <label className="va-sub" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  )
}

export function VendorAdd({ me }: { me: Me }) {
  const [level, setLevelState] = useState<Level>(readLevel)
  const [advOpen, setAdvOpen] = useState(false)
  const [f, setF] = useState<AddForm>(() => ({ ...EMPTY_ADD, ownerUserId: me.subject }))
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof AddForm, string>>>({})
  const [tried, setTried] = useState(false)
  const [general, setGeneral] = useState('')
  // The vendors added from this form with "Add another", pinned together on
  // the register when the person finally lands there.
  const [addedHere, setAddedHere] = useState<{ id: string; name: string }[]>([])
  const [focusFirst, setFocusFirst] = useState(0)
  const people = usePeopleContacts()

  const guessed = useMemo(() => guessLegalForm(f.legalName, f.country), [f.legalName, f.country])
  const legalForm = f.legalFormTouched ? f.legalForm : guessed
  const held = heldLine(f, level, legalForm)
  const registration = registrationField(legalForm)

  function setLevel(next: Level) {
    setLevelState(next)
    writeLevel(next)
  }

  function set<K extends keyof AddForm>(k: K, v: AddForm[K]) {
    setF((cur) => ({ ...cur, [k]: v }))
    setErrors((cur) => {
      if (!(k in cur)) return cur
      const next = { ...cur }
      delete next[k]
      return next
    })
  }

  // The mobile box means a different thing on each side of this choice: ten
  // digits for India, the international form for anywhere else. A number
  // already typed is carried across rather than silently reread.
  function changeCountry(country: string) {
    const typed = f.contactMobile.trim()
    let mobile = typed
    if (country === 'IN' && f.country !== 'IN') {
      const digits = typed.replace(/^\+91/, '').replace(/\D/g, '')
      mobile = typed.startsWith('+91') && INDIAN_MOBILE.test(digits) ? digits : ''
    } else if (country !== 'IN' && f.country === 'IN') {
      mobile = mobileForApi('IN', typed)
    }
    set('country', country)
    set('contactMobile', mobile)
  }

  // D2 to D4 (release 3.3, M4): amber on the field once the person has left
  // the dates, never blocking the save.
  const [datesLeft, setDatesLeft] = useState(false)
  const startWarn = datesLeft ? warnContractStartFar(f.contractStartDate, todayIndia()) : ''
  const endWarn = datesLeft
    ? warnShortContract(f.contractStartDate, f.contractEndDate) || warnContractEnded(f.contractEndDate, todayIndia())
    : ''

  // D1 of the date rules, said on the contract end as soon as the person
  // leaves either date, not only when they press the button.
  function leaveDates() {
    setDatesLeft(true)
    const msg = checkContractDates(f.contractStartDate, f.contractEndDate)
    setErrors((cur) => {
      if ((cur.contractEndDate ?? '') === msg) return cur
      const next = { ...cur }
      if (msg) next.contractEndDate = msg
      else delete next.contractEndDate
      return next
    })
  }

  // An error on a field the person cannot see is one they cannot fix: the
  // fold opens, or the level rises, to show it, and focus goes to it.
  function showErrors(e: Partial<Record<keyof AddForm, string>>) {
    setErrors(e)
    const need = levelForErrors(e, level)
    if (need === 'expert' && level !== 'expert') setLevel('expert')
    else if (need === 'advanced' && level === 'basic') setAdvOpen(true)
    setFocusFirst((n) => n + 1)
  }

  useEffect(() => {
    if (!focusFirst) return
    // The first wrong field down the page, not the first check that ran: a
    // bad mobile number must not take focus before a missing legal form above
    // it (review of 12 Oct, S2). With more than one, the summary takes focus
    // and names them all.
    const wrong = (Object.keys(errors) as (keyof AddForm)[])
      .map((k) => document.getElementById(fid(k)))
      .filter((el): el is HTMLElement => el !== null)
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
    if (wrong.length > 1) document.getElementById('va-errsum')?.focus()
    else wrong[0]?.focus()
    // Only when a submit was refused, not on every edit of the errors.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusFirst])

  // ------------------------------------------------------------- the tier

  const tierKey = JSON.stringify(tierInputs(f))
  const [tier, setTier] = useState<{ state: 'idle' | 'busy' | 'ok' | 'failed'; preview?: TierPreview }>({ state: 'idle' })
  useEffect(() => {
    let live = true
    setTier((t) => ({ ...t, state: 'busy' }))
    // Asked once the person pauses, not on every key.
    const timer = window.setTimeout(() => {
      api
        .tierPreview(JSON.parse(tierKey))
        .then((p) => live && setTier({ state: 'ok', preview: p }))
        .catch(() => live && setTier({ state: 'failed' }))
    }, 350)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [tierKey])

  // ------------------------------------------------------------- saving

  // Add and approve (M5): offered to a reader who may approve vendor requests,
  // when the organisation does not ask a second person to (lib/addVendor.ts).
  const mayApprove = can('vendor.request_approve')
  const [policy, setPolicy] = useState<VendorRequestPolicy | null>(null)
  useEffect(() => {
    if (!mayApprove) return
    let live = true
    api
      .vendorRequestPolicy()
      .then((p) => live && setPolicy(p))
      .catch(() => live && setPolicy(null))
    return () => {
      live = false
    }
  }, [mayApprove])
  const offerApprove = approveOffered(mayApprove, policy)

  async function submit(ev: FormEvent | null, another: boolean, approve = false) {
    ev?.preventDefault()
    setTried(true)
    const e = { ...checkAdd(f, todayIndia()), ...(approve ? checkApproveNow(legalForm) : {}) }
    setGeneral('')
    if (Object.keys(e).length > 0) {
      showErrors(e)
      return
    }
    setErrors({})
    setBusy(true)
    try {
      const created = await api.createVendor(createBody(f, legalForm, approve))
      try {
        await api.createContact(created.id, {
          role: 'PRIMARY',
          fullName: f.contactName.trim(),
          email: f.contactEmail.trim(),
          mobileE164: mobileForApi(f.country, f.contactMobile) || undefined,
          purpose: 'ASSESSMENT',
        })
      } catch (err) {
        // The vendor exists; the contact did not save. Land on the vendor so
        // nothing is lost and the person can add the contact by hand.
        setGeneral('The vendor was created but the contact could not be saved: ' + describe(err) + ' Add the contact on the vendor record.')
        window.location.hash = '#/vendor/' + created.id
        return
      }
      rememberAdded(created.id)
      if (another) {
        setAddedHere((cur) => [...cur, { id: created.id, name: f.legalName.trim() }])
        // The owner stays: the next vendor on a short list is usually the
        // same person's.
        setF({ ...EMPTY_ADD, ownerUserId: f.ownerUserId })
        setTried(false)
        document.getElementById(fid('legalName'))?.focus()
        return
      }
      window.location.replace(registerAfterAdding([...addedHere.map((a) => a.id), created.id]))
    } catch (err) {
      if (err instanceof ApiError && err.code === 'LEGAL_FORM_MISSING') {
        // Approving needs the legal form; nothing was created.
        showErrors({ legalForm: err.message })
      } else if (err instanceof ApiError && err.issues.length > 0) {
        const mapped: Partial<Record<keyof AddForm, string>> = {}
        const rest: string[] = []
        for (const i of err.issues as Issue[]) {
          const k = ISSUE_FIELD[i.field]
          if (k) mapped[k] = i.detail
          else rest.push(i.detail)
        }
        showErrors(mapped)
        setGeneral(rest.join(' '))
      } else {
        setGeneral(describe(err))
      }
    } finally {
      setBusy(false)
    }
  }

  // ------------------------------------------------------------- drawing

  const err = (k: keyof AddForm) =>
    errors[k] ? (
      <p className="va-err" id={fid(k) + '-err'}>
        {errors[k]}
      </p>
    ) : null
  const aria = (k: keyof AddForm, extra?: string) => ({
    id: fid(k),
    'aria-invalid': errors[k] ? true : undefined,
    'aria-describedby': [errors[k] ? fid(k) + '-err' : '', extra ?? ''].filter(Boolean).join(' ') || undefined,
  })
  const text = (k: keyof AddForm, props: Record<string, unknown> = {}) => (
    <input type="text" {...aria(k)} value={String(f[k] ?? '')} onChange={(e) => set(k, e.target.value as never)} {...props} />
  )
  const choose = (k: keyof AddForm, options: [string, string][], blank = 'Not stated') => (
    <select {...aria(k)} value={String(f[k] ?? '')} onChange={(e) => set(k, e.target.value as never)}>
      <option value="">{blank}</option>
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  )

  const owners = useMemo(() => {
    const mine = people[me.subject]
    const meLabel = 'Me' + (mine?.name ? ' (' + mine.name + ')' : mine?.email ? ' (' + mine.email + ')' : '')
    const others = Object.entries(people)
      .filter(([s]) => s !== me.subject)
      .map(([s, p]) => [s, p.name ? p.name + ' (' + p.email + ')' : p.email] as [string, string])
      .sort((a, b) => a[1].localeCompare(b[1]))
    return [[me.subject, meLabel] as [string, string], ...others]
  }, [people, me.subject])

  const problems = Object.keys(errors).length

  // The rows Advanced adds: folded under Basic, laid out as a section of
  // their own at Advanced and Expert.
  const advancedRows = (
    <>
    <Row label="Records and access" group="va-records-l">
      <div className="va-pair">
        <Half label="Records they hold" htmlFor={fid('volumeBand')}>
          {choose('volumeBand', [
            ['NONE', 'None'],
            ['UNDER_10K', 'Under 10,000'],
            ['UNDER_1M', 'Under 1 million'],
            ['OVER_1M', 'Over 1 million'],
          ], 'Not sure')}
        </Half>
        <Half label="Access to our systems" htmlFor={fid('systemAccess')}>
          {choose('systemAccess', [
            ['NONE', 'None'],
            ['READ', 'Read only'],
            ['WRITE', 'Can change data'],
            ['PRIVILEGED', 'Administrative'],
          ], 'Not sure')}
        </Half>
      </div>
    </Row>
    <Row label="Also true" group="va-flags-l">
      <div className="va-toggles">
        {FLAGS.map(([k, l]) => (
          <button key={k} type="button" aria-pressed={Boolean(f[k])} onClick={() => set(k, !f[k] as never)}>
            {l}
          </button>
        ))}
      </div>
    </Row>
    <Row label="Legal form and country" group="va-legal-l" hint={!f.legalFormTouched && guessed ? 'Legal form read from the name.' : undefined}>
      <div className="va-pair">
        <Half label="Legal form" htmlFor={fid('legalForm')}>
          <select
            {...aria('legalForm')}
            value={legalForm}
            onChange={(e) => {
              set('legalForm', e.target.value)
              set('legalFormTouched', true)
            }}
          >
            <option value="">Not known yet</option>
            {LEGAL_FORMS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          {err('legalForm')}
        </Half>
        <Half label="Country" htmlFor="va-country-pick">
          <div className="va-country">
            <select id="va-country-pick" value={f.country === 'IN' ? 'IN' : 'OTHER'} onChange={(e) => changeCountry(e.target.value === 'IN' ? 'IN' : '')}>
              <option value="IN">India</option>
              <option value="OTHER">Outside India</option>
            </select>
            {f.country !== 'IN' && (
              <input
                type="text"
                {...aria('country')}
                aria-label="Two-letter country code"
                placeholder="US"
                maxLength={2}
                value={f.country}
                onChange={(e) => set('country', e.target.value.toUpperCase())}
              />
            )}
          </div>
          {err('country')}
        </Half>
      </div>
    </Row>
    <Row label="Business function" htmlFor={fid('businessFunction')} optional>
      {text('businessFunction', { placeholder: 'For example Operations, Marketing, IT' })}
    </Row>
    <Row label="Contact mobile" htmlFor={fid('contactMobile')} optional>
      {f.country === 'IN' ? (
        <div style={{ display: 'flex' }}>
          {/* The country code is shown and never typed: the box takes
              the ten digits people write, and +91 goes in front on
              save. */}
          <span aria-hidden="true" style={PREFIX}>
            +91
          </span>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            autoComplete="off"
            {...aria('contactMobile', 'va-cmobile-cc')}
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, minWidth: 0 }}
            value={f.contactMobile}
            placeholder="9876543210"
            onChange={(e) => set('contactMobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
          />
          <span id="va-cmobile-cc" className="vh">
            10 digits, +91 is added
          </span>
        </div>
      ) : (
        <input type="tel" {...aria('contactMobile')} autoComplete="off" placeholder="+14155550100" value={f.contactMobile} onChange={(e) => set('contactMobile', e.target.value)} />
      )}
      {err('contactMobile')}
    </Row>
    <Row label="Contract" group="va-contract-l" optional hint="Leave the dates for later if the contract is not signed yet.">
      <div className="va-pair">
        <Half label="Starts" htmlFor={fid('contractStartDate')}>
          <input type="date" {...aria('contractStartDate', startWarn ? 'va-start-warn' : '')} value={f.contractStartDate} onChange={(e) => set('contractStartDate', e.target.value)} onBlur={leaveDates} />
          {err('contractStartDate')}
          <DateWarnNote id="va-start-warn" message={errors.contractStartDate ? '' : startWarn} />
        </Half>
        <Half label="Ends" htmlFor={fid('contractEndDate')}>
          <input type="date" {...aria('contractEndDate', endWarn ? 'va-end-warn' : '')} value={f.contractEndDate} onChange={(e) => set('contractEndDate', e.target.value)} onBlur={leaveDates} />
          {err('contractEndDate')}
          <DateWarnNote id="va-end-warn" message={errors.contractEndDate ? '' : endWarn} />
        </Half>
      </div>
    </Row>
    </>
  )

  return (
    <div className="p1-page va-page" data-level={level}>
      <div className="p1-head va-head">
        <div>
          <h1>Add a vendor</h1>
        </div>
        <div className="va-levels" role="group" aria-label="How much to fill in now">
          {LEVELS.map((l) => (
            <button key={l.key} type="button" aria-pressed={level === l.key} title={l.what} onClick={() => setLevel(l.key)}>
              {l.label}
            </button>
          ))}
        </div>
      </div>
      {addedHere.length > 0 && (
        <p className="note ok va-added" role="status">
          {addedHere.length === 1
            ? addedHere[0]?.name + ' added.'
            : addedHere.length + ' vendors added: ' + addedHere.map((a) => a.name).join(', ') + '.'}
        </p>
      )}

      <div className="va-layout">
        <form id="va-form" className="va-form" onSubmit={(e) => submit(e, false)} noValidate>
          {tried && Object.keys(errors).length > 0 && (
            <div id="va-errsum" className="va-errsum" role="alert" tabIndex={-1} aria-labelledby="va-errsum-h">
              <h2 id="va-errsum-h">{Object.keys(errors).length === 1 ? '1 field needs attention' : Object.keys(errors).length + ' fields need attention'}</h2>
              <ul>
                {(Object.keys(errors) as (keyof AddForm)[]).map((k) => (
                  <li key={k}>
                    <button
                      type="button"
                      onClick={() => {
                        showErrors(errors)
                        window.setTimeout(() => document.getElementById(fid(k))?.focus(), 0)
                      }}
                    >
                      {fieldLabel(k)}
                    </button>
                    : {errors[k]}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <section className="va-table" aria-labelledby="va-s-basic">
            <h2 id="va-s-basic">The vendor</h2>
            <Row label="Vendor name" htmlFor={fid('legalName')}>
              {text('legalName', { autoFocus: true, autoComplete: 'organization', 'aria-required': true, placeholder: 'As registered' })}
              {err('legalName')}
            </Row>
            <Row label="Vendor type" htmlFor={fid('vendorType')}>
              <select {...aria('vendorType')} aria-required="true" value={f.vendorType} onChange={(e) => set('vendorType', e.target.value)}>
                <option value="">Choose</option>
                {VENDOR_TYPES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              {err('vendorType')}
            </Row>
            <Row label="Business owner" htmlFor={fid('ownerUserId')}>
              <select {...aria('ownerUserId')} aria-required="true" value={f.ownerUserId} onChange={(e) => set('ownerUserId', e.target.value)}>
                {owners.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              {err('ownerUserId')}
            </Row>
            <Row label="Service provided" htmlFor={fid('serviceDescription')} optional>
              {text('serviceDescription', { placeholder: 'One line, in the words the business uses' })}
            </Row>
            <Row label="Vendor contact" group="va-contact-l" hint="The questionnaire goes to this person.">
              <div className="va-pair">
                <Half label="Name" htmlFor={fid('contactName')}>
                  {text('contactName', { 'aria-required': true, autoComplete: 'off' })}
                  {err('contactName')}
                </Half>
                <Half label="E-mail" htmlFor={fid('contactEmail')}>
                  <input
                    type="email"
                    {...aria('contactEmail')}
                    aria-required="true"
                    autoComplete="off"
                    placeholder="name@vendor.com"
                    value={f.contactEmail}
                    onChange={(e) => set('contactEmail', e.target.value)}
                  />
                  {err('contactEmail')}
                </Half>
              </div>
            </Row>
            <Row
              label={
                <span className="va-label-tip">
                  Data they will touch
                  <Toggletip hover label="About the data">
                    This decides how closely the vendor is looked at.
                  </Toggletip>
                </span>
              }
              group="va-data-l"
            >
              <div className="va-toggles">
                <button type="button" aria-pressed={f.dataCategories.length === 0} onClick={() => set('dataCategories', toggleData(f.dataCategories, 'NONE'))}>
                  None
                </button>
                {DATA_KINDS.map(([v, l]) => (
                  <button key={v} type="button" aria-pressed={f.dataCategories.includes(v)} onClick={() => set('dataCategories', toggleData(f.dataCategories, v))}>
                    {l}
                  </button>
                ))}
              </div>
            </Row>
          </section>

          {level === 'basic' ? (
            <Disclosure
              title="Advanced: tiering, legal form and country, contract dates"
              summary={advancedSummary(f, legalForm)}
              open={advOpen}
              onToggle={setAdvOpen}
            >
              <div className="va-table va-table-flat">{advancedRows}</div>
            </Disclosure>
          ) : (
            <section className="va-table" aria-labelledby="va-s-advanced">
              <h2 id="va-s-advanced">Tiering, legal form and country, contract</h2>
              {advancedRows}
            </section>
          )}

          {level === 'expert' && (
            <>
              <section className="va-table" aria-labelledby="va-s-statutory">
                <h2 id="va-s-statutory">Statutory numbers</h2>
                <p className="va-section-note">Any left blank are recorded as missing and asked for before approval.</p>
                <Row label="PAN" htmlFor={fid('pan')} optional>
                  {text('pan', { maxLength: 10, autoComplete: 'off', placeholder: 'ABCDE1234F' })}
                  {err('pan')}
                </Row>
                {registration && (
                  <Row label={registration === 'cin' ? 'CIN' : 'LLPIN'} htmlFor={fid(registration)} optional>
                    {text(registration, { autoComplete: 'off' })}
                    {err(registration)}
                  </Row>
                )}
                <Row label="GSTIN" htmlFor={fid('gstin')} optional>
                  {text('gstin', { maxLength: 15, autoComplete: 'off', placeholder: '27ABCDE1234F1Z5' })}
                  {err('gstin')}
                </Row>
                <Row label="MSME" group="va-msme-l" optional>
                  <div className="va-pair">
                    <Half label="Category" htmlFor={fid('msmeCategory')}>
                      {choose('msmeCategory', [
                        ['MICRO', 'Micro'],
                        ['SMALL', 'Small'],
                        ['MEDIUM', 'Medium'],
                      ], 'Not an MSME, or not known')}
                      {err('msmeCategory')}
                    </Half>
                    <Half label="Udyam number" htmlFor={fid('udyamNumber')}>
                      {text('udyamNumber', { autoComplete: 'off' })}
                      {err('udyamNumber')}
                    </Half>
                  </div>
                </Row>
              </section>
              <section className="va-table" aria-labelledby="va-s-address">
                <h2 id="va-s-address">Address and bank</h2>
                <Row label="Registered address" htmlFor={fid('addressLine1')} optional>
                  {text('addressLine1')}
                </Row>
                <Row label="City and PIN" group="va-city-l" optional>
                  <div className="va-pair">
                    <Half label="City" htmlFor={fid('city')}>
                      {text('city')}
                    </Half>
                    <Half label="PIN code" htmlFor={fid('pinCode')}>
                      {text('pinCode', { inputMode: 'numeric', maxLength: 6 })}
                      {err('pinCode')}
                    </Half>
                  </div>
                </Row>
                <Row label="Bank" group="va-bank-l" optional>
                  <div className="va-pair">
                    <Half label="IFSC" htmlFor={fid('ifsc')}>
                      {text('ifsc', { maxLength: 11, autoComplete: 'off' })}
                      {err('ifsc')}
                    </Half>
                    <Half label="Account number" htmlFor={fid('accountNumber')}>
                      {text('accountNumber', { inputMode: 'numeric', autoComplete: 'off' })}
                      {err('accountNumber')}
                    </Half>
                  </div>
                </Row>
              </section>
              <section className="va-table" aria-labelledby="va-s-profile">
                <h2 id="va-s-profile">Profile</h2>
                <Row label="Category" htmlFor={fid('vendorCategory')} optional>
                  {text('vendorCategory', { placeholder: 'Your own grouping, for example Printing' })}
                </Row>
                <Row label="Relationship start" htmlFor={fid('relationshipStartDate')} optional>
                  <input type="date" {...aria('relationshipStartDate')} value={f.relationshipStartDate} onChange={(e) => set('relationshipStartDate', e.target.value)} />
                  {err('relationshipStartDate')}
                </Row>
                <Row label="Service criticality" htmlFor={fid('serviceCriticality')} optional>
                  {choose('serviceCriticality', [['CRITICAL', 'Critical'], ...HML])}
                </Row>
                <Row label="Impact if it fails" group="va-impact-l" optional>
                  <div className="va-pair">
                    <Half label="Regulatory impact" htmlFor={fid('regulatoryImpact')}>
                      {choose('regulatoryImpact', HML)}
                    </Half>
                    <Half label="Continuity dependency" htmlFor={fid('continuityDependency')}>
                      {choose('continuityDependency', HML)}
                    </Half>
                  </div>
                </Row>
                <Row label="Where data is processed" group="va-where-l" optional>
                  <div className="va-pair">
                    <Half label="Processing location" htmlFor={fid('processingLocation')}>
                      {text('processingLocation')}
                    </Half>
                    <Half label="Data centre location" htmlFor={fid('dataCentreLocation')}>
                      {text('dataCentreLocation')}
                    </Half>
                  </div>
                </Row>
              </section>
            </>
          )}

          {held.length > 0 && (
            <p className="va-held" role="note">
              Also saved, from Expert: {held.map((h) => h.label + ' ' + h.value).join(', ')}.{' '}
              <button type="button" className="link" onClick={() => setLevel('expert')}>
                Show
              </button>
            </p>
          )}

          {general && (
            <div className="note stop va-general" role="alert">
              {general}
            </div>
          )}
        </form>

        <aside className="va-tier" aria-labelledby="va-tier-h">
          <h2 id="va-tier-h">Proposed tier</h2>
          <div aria-live="polite">
            {tier.state === 'failed' ? (
              <p className="va-tier-why">The tier could not be worked out now. It is set when the vendor is saved.</p>
            ) : tier.preview && tier.preview.tier == null ? (
              <p className="va-tier-why">{tier.preview.note}</p>
            ) : tier.preview ? (
              <>
                <p className="va-tier-label">{tier.preview.tierLabel}</p>
                <p className="va-tier-why">{tier.preview.why}</p>
                {!tierAnswered(f) && <p className="va-tier-note">Nothing that decides the tier is answered yet: the data row, and Advanced.</p>}
              </>
            ) : (
              <p className="va-tier-why">Working it out</p>
            )}
          </div>
          <p className="va-tier-note">Set by your organisation's tier rules when the vendor is saved. The TPRM lead can change it.</p>
        </aside>
      </div>

      <StickyFooter
        label="Add a vendor"
        busy={busy}
        note={tried && problems > 0 ? (problems === 1 ? '1 field needs attention' : problems + ' fields need attention') : 'A vendor code is given automatically'}
        more={[
          ...(offerApprove
            ? [{ label: 'Add and approve request', onClick: () => void submit(null, false, true), actionId: 'tprm.vendor-add.a03' }]
            : []),
          { label: 'Add another', onClick: () => void submit(null, true) },
          { label: 'Cancel', href: addedHere.length > 0 ? registerAfterAdding(addedHere.map((a) => a.id)) : '#/vendors', tone: 'plain' },
        ]}
        primary={{
          label: busy ? 'Saving' : 'Add vendor',
          form: 'va-form',
          actionId: 'tprm.vendor-add.a01',
          actionAlso: 'tprm.vendor-add.a02',
        }}
      />
    </div>
  )
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message + (err.sentRemedy ? ' ' + err.sentRemedy : '')
  return err instanceof Error ? err.message : String(err)
}
